import React from 'react';
import styled, { StyledComponentClass } from 'styled-components';
import { histogram, range } from 'd3-array';
import { scaleLinear } from 'd3-scale';
import some from 'lodash.some';

import ConditionalWrapper from '../../atoms/conditional-wrapper';
import {
  AxisBottom,
  Group,
  HoverableGroup,
  ResponsiveSVG,
} from '../../atoms/graphs';
import { ToolTip } from '../../components/tool-tip';
import { horizontalStack, packed } from '../../utils/layout';
import { DotHistogramProps, Quartile } from '../../types/graphs';
import {
  circleFromSquare,
  maxPackingEdge,
  quartiles as _quartiles,
} from './utils';
import { colors, quartileColorStyles } from '../../styles';

const noop = () => undefined;

class DotHistogram extends React.Component<DotHistogramProps, any> {
  static defaultProps: DotHistogramProps = {
    DotComponent: ({ className, cx, cy, r }) => (
      <circle {...{ className, cx, cy, r }} />
    ),
    TooltipComponent: ({ text }) => <div>{text}</div>,
    QuartileLegend: ({ data }) => <text>{data}</text>,
    axisPadding: 10,
    data: [],
    domain: [0, 100],
    isFocused: d => false,
    height: 200,
    memberPadding: 0,
    quartileHovered: noop,
    quartileUnhovered: noop,
    showAxis: true,
    showLegend: false,
    thresholds: range(5, 100, 5),
    tooltipProps: d => ({ text: d }),
    width: 600,
    value: d => d,
  };

  // TODO https://nautodev.atlassian.net/browse/NAUTO-11483
  // tslint:disable-next-line:cognitive-complexity
  public render() {
    const {
      DotComponent,
      DotWrapper,
      TooltipComponent,
      QuartileLegend,
      axisPadding,
      className,
      data,
      domain,
      isFocused,
      height,
      memberPadding,
      quartileHovered,
      quartileUnhovered,
      showAxis,
      showLegend,
      thresholds,
      tooltipProps,
      entityType,
      value,
      width,
    } = this.props;

    const axisHeight = showAxis ? 25 + axisPadding : 0;
    const legendHeight = showLegend ? 40 : 0;
    const hoverHeight = height - axisHeight;
    const graphHeight = hoverHeight - legendHeight;
    const anyFocused = some(data.map(isFocused));

    const xScale = scaleLinear()
      .domain(domain)
      .range([0, width]);
    const yScale = scaleLinear()
      .domain([0, height])
      .range([0, -height]);
    const buckets: any[] = histogram()
      .thresholds(thresholds)
      .value(value)
      .domain(domain)(data);
    const quartiles = _quartiles(buckets);

    const quartilesAlongGraph = horizontalStack(
      quartiles.length,
      hoverHeight,
      width,
    );
    const bucketsInQuartile = horizontalStack(
      quartiles.memberLength,
      hoverHeight,
      quartilesAlongGraph.memberWidth,
    );
    const maxBucketCount = Math.max(...buckets.map(b => b.length));
    const maximalDiameter = maxPackingEdge(
      graphHeight,
      bucketsInQuartile.memberWidth,
      maxBucketCount,
    );
    const shapeInBucket = bucketLength =>
      packed(
        maximalDiameter,
        bucketLength,
        graphHeight,
        bucketsInQuartile.memberWidth,
        memberPadding,
      );

    return (
      <ResponsiveSVG
        className={className}
        height={height}
        width={width}
        viewBox={`0 0 ${width} ${height}`}
      >
        <g>
          <HoverableGroup className="graph" {...{ height: graphHeight, width }}>
            {graphHovered =>
              quartiles.map((quartile, i: Quartile) => {
                const hoverIn = () => quartileHovered(quartile);
                const hoverOut = () => quartileUnhovered(quartile);

                return (
                  <HoverableGroup
                    className="quartile"
                    key={`quartile-${i}`}
                    x={quartilesAlongGraph(i).x}
                    height={quartilesAlongGraph(i).height}
                    width={quartilesAlongGraph(i).width}
                    hoverIn={hoverIn}
                    hoverOut={hoverOut}
                  >
                    {quartileIsHovered => {
                      const muted =
                        (graphHovered && !quartileIsHovered) || anyFocused;

                      return [
                        showLegend ? (
                          <QuartileLegend
                            key={`legend-${i}`}
                            data={quartile}
                            quartile={i}
                            isActive={quartileIsHovered}
                            width={quartilesAlongGraph.memberWidth}
                          />
                        ) : null,
                        quartile.map((bucket, j) => (
                          <Group
                            className="bucket"
                            key={`bucket-${j}`}
                            x={bucketsInQuartile(j).x}
                            y={bucketsInQuartile(j).height}
                          >
                            {bucket.map((datum, k) => {
                              const shape = shapeInBucket(bucket.length)(k);
                              const { cx, cy, r } = circleFromSquare(shape);
                              const focused = isFocused(datum);
                              const Tooltip = (
                                <TooltipComponent
                                  {...{ ...tooltipProps(datum), entityType }}
                                />
                              );
                              const classes = [
                                `quartile-${i}`,
                                muted ? 'muted' : '',
                                focused ? 'focused' : '',
                              ];
                              const dotProps = {
                                className: classes.join(' '),
                                cx,
                                cy: yScale(cy),
                                r,
                                data: datum,
                                quartile: i,
                                focused,
                                muted,
                              };

                              return (
                                <ToolTip
                                  key={`shape-${k}`}
                                  tooltip={Tooltip}
                                  svg={true}
                                >
                                  <ConditionalWrapper
                                    Wrapper={DotWrapper}
                                    {...dotProps}
                                  >
                                    <DotComponent {...dotProps} />
                                  </ConditionalWrapper>
                                </ToolTip>
                              );
                            })}
                          </Group>
                        )),
                      ];
                    }}
                  </HoverableGroup>
                );
              })
            }
          </HoverableGroup>
          {showAxis && (
            <AxisBottom
              scale={xScale}
              top={hoverHeight}
              padding={axisPadding}
              hideEnds={true}
            />
          )}
        </g>
      </ResponsiveSVG>
    );
  }
}

const StyledDotHistogram = styled(DotHistogram)`
  ${quartileColorStyles('fill')};

  circle.muted:not(.focused) {
    fill: ${colors.grey100};
  }
`;

export default StyledDotHistogram;
